/**
 * AuthCard — the Organic card family for every pre-auth surface that renders
 * inside AuthShell (sign-in, sign-up, join, forgot/reset password,
 * verify-email, beta).
 *
 * Purely visual. It composes the shared `Card` primitive so radius and
 * surface tokens stay single-sourced in @batch-and-bowl/ui, and layers the
 * treatment the reskinned app pages already use:
 *
 *   - borderless sand surface with soft elevation (Preferences QuestionCard:
 *     hierarchy from tone and shadow, not a border);
 *   - a small uppercase terracotta kicker (PageHeader's kicker string);
 *   - a real `h1` title — the card IS the page inside AuthShell, so its
 *     heading is the document's primary heading. Caprasimo comes from the
 *     global heading rule in theme.css; it ships weight 400 only, so no
 *     `font-bold` here (font-synthesis-weight is off).
 *
 * The API mirrors the Card family one-to-one so call sites swap imports
 * without restructuring. No state, no data, no business logic.
 */
import * as React from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@batch-and-bowl/ui/card";
import { cn } from "@batch-and-bowl/ui/utils";

// Same string PageHeader and SettingsSection use, so the kicker reads
// identically before and after sign-in. terracotta-700/300 are the AA-safe
// ramp steps for small text; never raw --primary here.
const KICKER_CLASS =
  "text-xs font-semibold uppercase tracking-[0.1em] text-terracotta-700 dark:text-terracotta-300";

export function AuthCard({
  className,
  ...props
}: React.ComponentProps<typeof Card>) {
  return (
    <Card className={cn("w-full border-0 shadow-sm", className)} {...props} />
  );
}

export function AuthCardHeader({
  className,
  ...props
}: React.ComponentProps<typeof CardHeader>) {
  return <CardHeader className={cn("text-center", className)} {...props} />;
}

export function AuthCardKicker({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="auth-card-kicker"
      className={cn(KICKER_CLASS, className)}
      {...props}
    />
  );
}

export function AuthCardTitle({
  className,
  ...props
}: React.ComponentProps<"h1">) {
  return (
    <h1
      data-slot="card-title"
      className={cn("text-2xl text-foreground", className)}
      {...props}
    />
  );
}

export function AuthCardDescription({
  className,
  ...props
}: React.ComponentProps<typeof CardDescription>) {
  return <CardDescription className={cn("text-sm", className)} {...props} />;
}

export const AuthCardContent = CardContent;
export const AuthCardFooter = CardFooter;
